import { ApiError } from '@/api/errors';
import { UserFacingUploadError } from './photoFiles';

/**
 * One Dutch sentence describing why an upload batch partially failed.
 *
 * The reason matters more than the count: a user who picked a `.docx` needs to
 * learn which file types are allowed, and that text already exists — either in
 * the API's `{ message }` envelope or in the client-side pre-check. Both used to
 * be discarded by `uploadPool`, leaving only "1 foto('s) niet geupload".
 *
 * Shows the reason only when every failure agrees on one; a mixed batch would
 * otherwise pick an arbitrary file's error and present it as the whole story.
 */
export function describeUploadFailures(failed: number, failures: unknown[]): string {
  const noun = failed === 1 ? '1 bestand' : `${failed} bestanden`;
  const reasons = new Set(failures.map(reasonOf).filter((r): r is string => r !== null));
  const reason = reasons.size === 1 ? [...reasons][0] : null;
  return reason ? `${noun} niet geüpload: ${reason}` : `${noun} niet geüpload`;
}

/**
 * The user-showable Dutch reason inside a thrown error, or null.
 *
 * An `ApiError` message is the server's own envelope text and is already Dutch.
 * Anything else is only trusted when it is one of ours — a raw `Error` from the
 * canvas or the network carries English internals ("Failed to load image",
 * "NetworkError when attempting to fetch resource") that must not reach a user.
 */
function reasonOf(err: unknown): string | null {
  if (err instanceof ApiError) return err.message || null;
  if (err instanceof UserFacingUploadError) return err.message;
  return null;
}
